import { WS_BASE_URL } from "../../../../environment";
import { NewLeadWebSocketMessage } from "./chatTypes";

type MessageCallback = (message: NewLeadWebSocketMessage) => void;

export class NewLeadWebSocketService {
    private ws: WebSocket | null = null;
    private tenantId: number | null = null;
    private messageCallbacks: MessageCallback[] = [];
    private reconnectAttempts = 0;
    private maxReconnectAttempts = 5;
    private reconnectDelay = 3000;
    private isIntentionallyClosed = false;
    private reconnectTimer: any = null;

    connect(tenantId: number): void {
        if (this.reconnectTimer) {
            clearTimeout(this.reconnectTimer);
            this.reconnectTimer = null;
        }

        if (this.ws) {
            const wsToClose = this.ws;
            this.ws = null;
            wsToClose.onmessage = null;
            wsToClose.onerror = null;
            wsToClose.onclose = null;
            if (wsToClose.readyState === WebSocket.CONNECTING) {
                wsToClose.onopen = () => {
                    try {
                        wsToClose.close();
                    } catch (_) {}
                };
            } else {
                try {
                    wsToClose.close();
                } catch (_) {}
            }
        }
        
        this.tenantId = tenantId;
        this.isIntentionallyClosed = false;

        let wsUrl = `${WS_BASE_URL}/ws/leads/${tenantId}`;

        if (wsUrl.startsWith("https://")) {
            wsUrl = wsUrl.replace("https://", "wss://");
        } else if (wsUrl.startsWith("http://")) {
            wsUrl = wsUrl.replace("http://", "ws://");
        }

        try {
            const currentWs = new WebSocket(wsUrl);
            this.ws = currentWs;

            this.ws.onopen = () => {
                this.reconnectAttempts = 0;
            };

            this.ws.onmessage = (event) => {
                try {
                    const message: NewLeadWebSocketMessage = JSON.parse(event.data);
                    this.messageCallbacks.forEach((callback) => callback(message));
                } catch (error) {
                }
            };

            this.ws.onerror = (error) => {
            };

            this.ws.onclose = () => {
                if (this.ws !== currentWs) return;

                if (!this.isIntentionallyClosed && this.reconnectAttempts < this.maxReconnectAttempts) {
                    this.reconnectAttempts++;
                    this.reconnectTimer = setTimeout(() => {
                        if (this.tenantId) {
                            this.connect(this.tenantId);
                        }
                    }, this.reconnectDelay);
                }
            };
        } catch (error) {
        }
    }

    disconnect(): void {
        this.isIntentionallyClosed = true;

        if (this.reconnectTimer) {
            clearTimeout(this.reconnectTimer);
            this.reconnectTimer = null;
        }

        if (this.ws) {
            const wsToClose = this.ws;
            this.ws = null;
            wsToClose.onmessage = null;
            wsToClose.onerror = null;
            wsToClose.onclose = null;
            if (wsToClose.readyState === WebSocket.CONNECTING) {
                wsToClose.onopen = () => {
                    try {
                        wsToClose.close();
                    } catch (_) {}
                };
            } else {
                try {
                    wsToClose.close();
                } catch (_) {}
            }
        }

        this.messageCallbacks = [];
        this.tenantId = null;
        this.reconnectAttempts = 0;
    }

    onMessage(callback: MessageCallback): void {
        this.messageCallbacks.push(callback);
    }

    offMessage(callback: MessageCallback): void {
        this.messageCallbacks = this.messageCallbacks.filter((cb) => cb !== callback);
    }

    isConnected(): boolean {
        return this.ws !== null && this.ws.readyState === WebSocket.OPEN;
    }
}

export const newLeadWebSocketService = new NewLeadWebSocketService();
